/* eslint-disable @typescript-eslint/no-explicit-any */
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { configureCustomFieldSuggestions } from './customFieldSuggestions';
import { formatEditorHtmlToMessageText, formatMessage } from './helpers';
import { messageHasCustomFieldsWithoutDefaultValue } from './templateFieldValidation';
import { configureMessageTemplates } from './messageTemplateSuggestions';
import { EditShortLinkModal } from 'src/modules/shortLinks/components/EditShortLinkModal/EditShortLinkModal';

import './styles.scss';
import { LinkMention } from './LinkMention';
import { PlusCircle } from 'phosphor-react';

export type TemplateTextProps = {
  text: string;
  messageTemplates: MessageTemplate[] | undefined;
  customFields: DefaultCustomField[];
  disabled?: boolean;
  clearText?: () => void;
  updateText: (newText: string) => void;
  emodjiPickerPosition?: { top?: string; bottom?: string; right: string };
  /**
   * Float the emoji picker over the page instead of placing it inside the editor, so a
   * scrolling container such as a dialog does not grow a scrollbar to fit it.
   */
  floatingEmojiPicker?: boolean;
  showConfirmationText: boolean;
  confirmationText: string;
  setFocus: boolean;
  note?: ReactNode;
  height?: string;
  minHeight?: string;
  padding?: string;
  onAttachFile?: () => void;
  showAttachFile?: boolean;
  onEnterPress?: (newText: string) => void;
  handleFocus?: () => void;
  handleBlur?: () => void;
  hideMessageTemplates?: boolean;
  hasError?: boolean;
  hideToolButtons?: boolean;
  hideAbsentDefaultValueAlert?: boolean;
  /** When true, tool buttons are not rendered inside the editor. Pair with toolbarContainer. */
  externalToolbar?: boolean;
  /** Host element for tool buttons when externalToolbar is true. */
  toolbarContainer?: Element | null;
};

export default function TemplateText(props: TemplateTextProps) {
  const {
    text,
    updateText,
    messageTemplates,
    customFields,
    disabled,
    setFocus,
    confirmationText,
    showConfirmationText,
    emodjiPickerPosition,
    floatingEmojiPicker = false,
    note,
    clearText,
    minHeight,
    padding,
    onAttachFile,
    showAttachFile,
    onEnterPress,
    handleFocus,
    handleBlur,
    hideMessageTemplates,
    hasError,
    hideToolButtons,
    hideAbsentDefaultValueAlert,
    externalToolbar,
    toolbarContainer,
  } = props;
  const isExternalToolbar = Boolean(externalToolbar);

  const [openEmojiModal, setOpenEmojiModal] = useState<boolean>(false);
  const editorBoxRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [openUrlShortenerModal, setOpenUrlShortenerModal] = useState<boolean>(false);
  const [caretPosition, setCaretPosition] = useState<number>(1);
  const [hasFieldsWithoutDefaultValue, setHasFieldsWithoutDefaultValue] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isBoldActive, setIsBoldActive] = useState<boolean>(false);
  const [isItalicActive, setIsItalicActive] = useState<boolean>(false);
  
  const theme = useTheme();
  const messageTemplateIconRef = useRef<HTMLButtonElement>(null);
  const mobileMenuIconRef = useRef<HTMLButtonElement>(null);
  const emojiTriggerRangeRef = useRef<{ from: number; to: number } | null>(null);
  // Keep latest customFields available to the editor's suggestion plugin.
  // useEditor is created once, so reading the prop directly would capture a
  // stale (often empty) array when fields load asynchronously.
  const customFieldsRef = useRef(customFields);
  customFieldsRef.current = customFields;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasCustomFieldsWithoutDefaultValue = (message: string): void => {
    setHasFieldsWithoutDefaultValue(
      messageHasCustomFieldsWithoutDefaultValue(message, customFields),
    );
  };

  const handleMobileMenuOpen = () => {
    window.setTimeout(() => {
      setIsMobileMenuOpen(true);
    }, 100);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleInsertShortUrl = (shortUrl: string) => {
    if (editor) {
      // Insert the link mention as HTML that matches the LinkMention extension format
      const linkMentionHtml = `<span data-type="linkMention" data-id="${shortUrl}" data-label="${shortUrl}" data-href="${shortUrl}" class="mention mention-link" contenteditable="false">${shortUrl}</span> `;

      editor
        ?.chain()
        .focus()
        .insertContentAt(caretPosition, linkMentionHtml)
        .focus()
        .run();

      // Update text with the new content
      setTimeout(() => {
        const html = editor.getHTML();
        updateText(html);
        editor?.commands.focus();
      }, 100);
    }
  };

  const syncTextStyleButtons = (targetEditor: Editor) => {
    setIsBoldActive(targetEditor.isActive('bold'));
    setIsItalicActive(targetEditor.isActive('italic'));
  };

  const toolbarIconButtonSx = {
    minWidth: '28px',
    padding: '2px',
    width: '28px',
    height: '28px',
    margin: 0,
  };

  const getFormatButtonSx = (isActive: boolean, marginRight: string = '0px') => ({
    ...toolbarIconButtonSx,
    marginRight,
    borderRadius: 1,
    backgroundColor: isActive ? theme.palette.primary.light : 'transparent',
    '&:hover': {
      backgroundColor: isActive ? theme.palette.primary.light : alpha(theme.palette.primary.main, 0.08),
    },
  });

  const toggleBold = () => {
    if (!editor) {
      return;
    }

    editor.chain().focus().toggleBold().run();
    syncTextStyleButtons(editor);
  };

  const toggleItalic = () => {
    if (!editor) {
      return;
    }

    editor.chain().focus().toggleItalic().run();
    syncTextStyleButtons(editor);
  };

  const replaceLinksToMentions = (editor: Editor) => {
    const html = editor.getHTML();

    if (html.includes('class="mention mention-link"') || html.includes('href=') || html.includes('data-type="linkMention"')) {
      return;
    }

    let newHtml = html;
    const host = window.location.host;
    const hostRegex = new RegExp(`(?:https?://)?${host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^\\s<>"{}]*`, 'g');

    newHtml = html.replace(hostRegex, match => {
      if (
        match.includes('class="mention"') ||
        match.includes('data-type="mention"') ||
        match.includes('href=')
      ) {
        return match;
      }
      return `<span data-label="${match}" data-type="linkMention" data-id="${match}" data-href="${match}" class="mention mention-link" contenteditable="false">${match}</span>`;
    });

    if (newHtml !== html) {
      editor.commands.setContent(newHtml);
      editor.commands.focus();
    }
  };

  const handleEditorClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;

    if (target.classList.contains('mention') && target.classList.contains('mention-link')) {
      const url = target.getAttribute('data-href') || target.getAttribute('data-url');
      if (url) {
        // Add visual feedback
        target.style.backgroundColor = theme.palette.info.lighter ?? theme.palette.info.light;
        target.style.color = theme.palette.info.dark;

        // Open the link
        window.open(url, '_blank', 'noopener,noreferrer');

        // Reset styling after a short delay
        setTimeout(() => {
          target.style.backgroundColor = '';
          target.style.color = '';
        }, 200);
      }
    } else {
      // Editor was clicked - trigger focus handler
      handleFocus?.();
    }
  };

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        document: false,
        dropcursor: false,
        gapcursor: false,
        hardBreak: false,
        heading: false,
        history: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
        paragraph: false,
        strike: false,
        text: false,
      }),
      LinkMention.configure({
        deleteTriggerWithBackspace: true,
      }),
      Text,
      History.configure({
        depth: 100,
        newGroupDelay: 500,
      }),
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            'Shift-Enter': () => this.editor.commands.setHardBreak(),
            Enter: () => {
              if (onEnterPress) {
                onEnterPress(formatEditorHtmlToMessageText(this.editor.getHTML()));
              } else {
                this.editor.commands.setHardBreak();
              }
              return true;
            },
            'Mod-z': () => this.editor.commands.undo(),
            'Mod-Shift-z': () => this.editor.commands.redo(),
            'Mod-y': () => this.editor.commands.redo(),
          };
        },
      }),
      ...(hideMessageTemplates ? [] : [
        Mention.extend({
          name: 'messageTemplateMention',
        }).configure({
          deleteTriggerWithBackspace: true,
          HTMLAttributes: {
            class: 'mention',
          },
          renderText({ node }) {
            return `${node.attrs.label}`;
          },
          suggestion: configureMessageTemplates(messageTemplates, customFields),
        }),
      ]),
      Mention.extend({
        name: 'customFieldMention',
      }).configure({
        deleteTriggerWithBackspace: true,
        HTMLAttributes: {
          class: 'mention mention-custom-field',
        },
        renderHTML({ node }) {
          const { id, label } = node.attrs;
          return [
            'span',
            {
              'data-id': id,
              'data-label': label,
              'data-type': 'customFieldMention',
              class: 'mention mention-custom-field',
              contenteditable: 'false',
            },
            `${label}`,
          ];
        },
        suggestion: configureCustomFieldSuggestions(customFieldsRef),
      }),
    ],
    onTransaction: ({ transaction }) => {
      setCaretPosition(transaction.selection.anchor);
      if (editor) {
        syncTextStyleButtons(editor);
      }
    },
    onSelectionUpdate({ editor }) {
      syncTextStyleButtons(editor);
    },
    onUpdate({ editor }) {
      if (editor) {
        const anchor = editor.state.selection.anchor;
        const triggerRange = { from: anchor - 1, to: anchor };

        if (anchor > 0 && isEmojiTriggerRange(editor, triggerRange)) {
          emojiTriggerRangeRef.current = triggerRange;
          setOpenEmojiModal(true);
        } else {
          setOpenEmojiModal(false);
        }

        hasCustomFieldsWithoutDefaultValue(editor.getHTML());

        const html = editor.getHTML();
        if (html && !html.includes('class="mention mention-link"') && !html.includes('href=')) {
          updateText(formatEditorHtmlToMessageText(html));
        } else {
          updateText(formatEditorHtmlToMessageText(html));
        }
      }
    },
    onCreate({ editor }) {
      const html = editor.getHTML();
      if (
        html &&
        html.trim() &&
        !html.includes('class="mention mention-link"') &&
        !html.includes('href=')
      ) {
        replaceLinksToMentions(editor);
      }
    },
    content: formatMessage(text || '', customFields),
    editorProps: {
      attributes: {
        enterkeyhint: 'send',
      },
      handleClickOn(view, pos, node, nodePos, event) {
        if (node.type.name === 'mention' && node.attrs.href) {
          // Optional: custom SPA routing logic
          const href = node.attrs.href as string;
          if (href.startsWith('/')) {
            event.preventDefault();
            // e.g. next/router:
            // router.push(href)
          }
          // Else let the native <a> handle it
        }
        return false;
      },
    },
  });

  const isEmojiTriggerRange = (
    targetEditor: Editor,
    range: { from: number; to: number },
  ) => {
    if (targetEditor.state.doc.textBetween(range.from, range.to) !== ':') {
      return false;
    }

    const textBeforeTrigger = targetEditor.state.doc.textBetween(0, range.from, '\n', '\n');

    return textBeforeTrigger.length === 0 || /[\s.]$/.test(textBeforeTrigger);
  };

  const getEmojiTriggerRange = useCallback(() => {
    if (!editor || !emojiTriggerRangeRef.current) {
      return null;
    }

    const { from, to } = emojiTriggerRangeRef.current;
    return isEmojiTriggerRange(editor, { from, to }) ? { from, to } : null;
  }, [editor]);

  const removeEmojiTrigger = useCallback(() => {
    const triggerRange = getEmojiTriggerRange();

    if (editor && triggerRange) {
      editor.commands.deleteRange(triggerRange);
    }

    emojiTriggerRangeRef.current = null;
  }, [editor, getEmojiTriggerRange]);

  const openEmojiPickerFromToolbar = () => {
    if (!editor) {
      return;
    }

    editor.commands.insertContentAt(caretPosition, ':');
    emojiTriggerRangeRef.current = { from: caretPosition, to: caretPosition + 1 };
    setOpenEmojiModal(true);
  };

  editor?.view.dom.addEventListener('copy', event => {
    const text = window?.getSelection()?.toString().replace('{', '');
    if (text) {
      event?.clipboardData?.setData('text/plain', text);
    }

    event.preventDefault();
  });

  useEffect(() => {
    if (editor) {
      if (setFocus && !disabled) {
        editor.commands.focus('end');
      }
      hasCustomFieldsWithoutDefaultValue(editor.getHTML());
      
      if (isMobile) {
        const editorElement = editor.view.dom as HTMLElement;
        if (editorElement) {
          editorElement.setAttribute('enterkeyhint', 'send');
          editorElement.setAttribute('inputmode', 'text');
          editorElement.setAttribute('autocapitalize', 'sentences');
          editorElement.setAttribute('autocorrect', 'on');
        }
      }
    }
  }, [editor, isMobile]);

  useEffect(() => {
    if (editor && !text) {
      editor.commands.clearContent();
      hasCustomFieldsWithoutDefaultValue('');
    }
  }, [text]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled]);

  useEffect(() => {
    if (editor && showConfirmationText) {
      if (!editor.getText().includes(confirmationText.replace('<br/>', ''))) {
        editor.commands.insertContent('<br />' + confirmationText);
        editor.commands.focus('start');
      }
    }
  }, [showConfirmationText]);

  useEffect(() => {
    if (!openEmojiModal) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      removeEmojiTrigger();
      setOpenEmojiModal(false);
      editor?.commands.focus();
    };

    document.addEventListener('keydown', handleEscape, true);

    return () => {
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [editor, openEmojiModal, removeEmojiTrigger]);

  const closeEmojiPicker = () => {
    removeEmojiTrigger();
    setOpenEmojiModal(false);
  };

  const emojiPicker = (
    <EmojiPicker
      style={{ zIndex: 9, width: '350px', height: '400px' }}
      previewConfig={{
        showPreview: false,
      }}
      onEmojiClick={(e: EmojiClickData) => {
        const emoji = e.emoji;
        editor?.commands.insertContentAt(
          getEmojiTriggerRange() || { from: caretPosition - 1, to: caretPosition },
          emoji,
        );
        editor?.commands.focus();
        emojiTriggerRangeRef.current = null;
        setOpenEmojiModal(false);
      }}
    />
  );

  const toolbarButtons = isMobile ? (
    <IconButton
      ref={mobileMenuIconRef}
      onClick={handleMobileMenuOpen}
      sx={{
        minWidth: '36px',
        padding: '6px',
        width: '36px',
        height: '36px',
        '&:hover': {
          backgroundColor: 'grey.200',
        },
      }}
    >
      <PlusCircle size={20} color={theme.palette.primary.main} />
    </IconButton>
  ) : (
    <>
      <Tooltip title="Bold" placement="top" arrow>
        <IconButton
          disabled={disabled}
          aria-pressed={isBoldActive}
          sx={getFormatButtonSx(isBoldActive, '0px')}
          style={{
            cursor: 'pointer',
          }}
          onClick={toggleBold}
        >
          <FormatBoldIcon color={!disabled || isBoldActive ? 'primary' : 'disabled'} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic" placement="top" arrow>
        <IconButton
          disabled={disabled}
          aria-pressed={isItalicActive}
          sx={getFormatButtonSx(isItalicActive, '0px')}
          style={{
            cursor: 'pointer',
          }}
          onClick={toggleItalic}
        >
          <FormatItalicIcon color={!disabled || isItalicActive ? 'primary' : 'disabled'} />
        </IconButton>
      </Tooltip>
      <IconButton
        disabled={disabled}
        sx={toolbarIconButtonSx}
        style={{
          backgroundColor: 'transparent',
          cursor: 'pointer',
        }}
        onClick={() => {
          setOpenUrlShortenerModal(true);
        }}
      >
        <InsertLinkIcon color={!disabled ? 'primary' : 'disabled'} />
      </IconButton>
      <IconButton
        disabled={disabled}
        sx={toolbarIconButtonSx}
        style={{
          backgroundColor: 'transparent',
          cursor: 'pointer',
        }}
        onClick={() => {
          editor?.commands.insertContentAt(caretPosition, ' {');
        }}
      >
        <LocalOfferOutlinedIcon color={'primary'} />
      </IconButton>

      {!hideMessageTemplates &&
        (messageTemplates && messageTemplates.length > 0 ? (
          <IconButton
            disabled={disabled}
            ref={messageTemplateIconRef}
            sx={toolbarIconButtonSx}
            style={{
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => {
              editor?.commands.insertContentAt(caretPosition, ' /');
              editor?.commands.focus('end');
            }}
          >
            <TextSnippetOutlinedIcon color="primary" />
          </IconButton>
        ) : (
          <Tooltip
            title="No templates available"
            placement="top"
            arrow
            PopperProps={{
              sx: {
                left: '-11px !important',
              },
            }}
          >
            <span>
              <IconButton
                disabled
                sx={toolbarIconButtonSx}
                style={{
                  backgroundColor: 'transparent',
                  cursor: 'not-allowed',
                }}
              >
                <TextSnippetOutlinedIcon color="disabled" />
              </IconButton>
            </span>
          </Tooltip>
        ))}

      <IconButton
        ref={emojiButtonRef}
        disabled={disabled}
        sx={toolbarIconButtonSx}
        style={{
          backgroundColor: 'transparent',
          cursor: 'pointer',
        }}
        onClick={() => {
          openEmojiPickerFromToolbar();
        }}
      >
        <InsertEmoticonIcon color={!disabled ? 'primary' : 'disabled'} />
      </IconButton>
      {showAttachFile && (
        <IconButton
          disabled={disabled}
          sx={toolbarIconButtonSx}
          style={{
            backgroundColor: 'transparent',
            cursor: 'pointer',
          }}
          onClick={onAttachFile}
        >
          <AttachFileIcon color={!disabled ? 'primary' : 'disabled'} />
        </IconButton>
      )}
    </>
  );

  const toolbar = (
    <Box
      {...(isExternalToolbar
        ? {}
        : {
            position: 'absolute' as const,
            right: '8px',
            bottom: clearText ? '25px' : '4px',
          })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        ...(isExternalToolbar
          ? {}
          : {
              backgroundColor: 'common.white',
              borderRadius: 1,
              zIndex: 1,
            }),
      }}
    >
      {toolbarButtons}
    </Box>
  );

  return (
    <>
      {hasFieldsWithoutDefaultValue && !hideAbsentDefaultValueAlert && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontStyle: 'italic',
              color: 'error.main',
            }}
          >
            This message contains merge tags without default values. Only contacts with specified
            values for these merge tags will receive this message.
          </Typography>
        </Box>
      )}
      <Box
        ref={editorBoxRef}
        sx={{
          height: '100%',
          minHeight: minHeight || '130px',
          position: 'relative',
          minWidth: isMobile ? '85vw' : '100%',
          maxWidth: isMobile ? '85vw' : '100%',
          border: hasError ? `2px solid ${theme.palette.error.main}` : '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: 1.5,
          '& .tiptap': {
            padding:
              padding ||
              (isExternalToolbar
                ? '8px'
                : isMobile
                  ? '12px 42px 12px 12px'
                  : '8px 8px 40px'),
            minHeight: minHeight || '130px',
            maxWidth: isMobile ? '85vw' : '100%',
            fontSize: isMobile ? '16px' : 'inherit',
            lineHeight: isMobile ? '1.5' : 'inherit',
            border: 'none',
            outline: 'none',
            '& .mention': {
              backgroundColor: theme.palette.info.lighter ?? theme.palette.info.light,
              color: theme.palette.info.dark,
              borderRadius: 1,
              boxDecorationBreak: 'clone',
              padding: '0.1rem 0.3rem',
            },
            [theme.breakpoints.down('sm')]: {
              '&:focus, &:focus-within': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '-1px',
                borderColor: theme.palette.primary.main,
              },
            },
          },
          '& .ProseMirror': {
            outline: 'none',
          },
          '& .ProseMirror:focus': {
            outline: 'none',
          },
          '& .ProseMirror-focused': {
            outline: `1px solid ${theme.palette.grey[400]}`,
            [theme.breakpoints.down('sm')]: {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '-1px',
            },
          },
        }}
      >
        {isMobile && !disabled && !hideToolButtons && !isExternalToolbar && (
          <Box
            sx={{
              position: 'absolute',
              right: '4px',
              bottom: showAttachFile ? '1px' : '20px',
              zIndex: 1,
            }}
          >
            <IconButton
              ref={mobileMenuIconRef}
              onClick={handleMobileMenuOpen}
              sx={{
                minWidth: '36px',
                padding: '6px',
                width: '36px',
                height: '36px',
                '&:hover': {
                  backgroundColor: 'grey.200',
                },
              }}
            >
              <PlusCircle size={20} color={theme.palette.primary.main} />
            </IconButton>
          </Box>
        )}

        <Menu
          anchorEl={mobileMenuIconRef.current}
          open={isMobileMenuOpen}
          onClose={handleMobileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem onClick={() => {
            toggleBold();
            handleMobileMenuClose();
          }} selected={isBoldActive}>
            <ListItemIcon>
              <FormatBoldIcon fontSize="small" color={isBoldActive ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText>Bold</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => {
            toggleItalic();
            handleMobileMenuClose();
          }} selected={isItalicActive}>
            <ListItemIcon>
              <FormatItalicIcon fontSize="small" color={isItalicActive ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText>Italic</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => {
            setOpenUrlShortenerModal(true);
            handleMobileMenuClose();
          }}>
            <ListItemIcon>
              <InsertLinkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Insert Link</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => {
            editor?.commands.insertContentAt(caretPosition, ' {');
            handleMobileMenuClose();
          }}>
            <ListItemIcon>
              <LocalOfferOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Insert Merge Tag</ListItemText>
          </MenuItem>

          {!hideMessageTemplates && messageTemplates && messageTemplates.length > 0 && (
            <MenuItem onClick={() => {
              editor?.commands.insertContentAt(caretPosition, ' /');
              editor?.commands.focus('end');
              handleMobileMenuClose();
            }}>
              <ListItemIcon>
                <TextSnippetOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Insert Template</ListItemText>
            </MenuItem>
          )}
          
          {!hideMessageTemplates && messageTemplates && messageTemplates.length === 0 && (
            <MenuItem disabled>
              <ListItemIcon>
                <TextSnippetOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>No Templates Available</ListItemText>
            </MenuItem>
          )}

          <MenuItem onClick={() => {
            openEmojiPickerFromToolbar();
            handleMobileMenuClose();
          }}>
            <ListItemIcon>
              <InsertEmoticonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Insert Emoji</ListItemText>
          </MenuItem>
          
          {showAttachFile && (
            <MenuItem onClick={() => {
              onAttachFile?.();
              handleMobileMenuClose();
            }}>
              <ListItemIcon>
                <AttachFileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Attach File</ListItemText>
            </MenuItem>
          )}
        </Menu>

        <EditorContent
          readOnly={!!disabled}
          disabled={!!disabled}
          editor={editor}
          style={{ 
            minHeight: minHeight || '130px',
            fontSize: isMobile ? '16px' : 'inherit',
          }}
          onClick={handleEditorClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {!disabled && (
          <Stack direction="column">
            {openEmojiModal && (
              <ClickAwayListener onClickAway={closeEmojiPicker}>
                {floatingEmojiPicker ? (
                  <Popper
                    open
                    anchorEl={emojiButtonRef.current ?? mobileMenuIconRef.current ?? editorBoxRef.current}
                    placement="top-end"
                    disablePortal
                    popperOptions={{ strategy: 'fixed' }}
                    modifiers={[{ name: 'flip', enabled: true }, { name: 'preventOverflow', enabled: true }]}
                    sx={{ zIndex: 9 }}
                  >
                    <Box>{emojiPicker}</Box>
                  </Popper>
                ) : (
                  <Box
                    sx={
                      emodjiPickerPosition
                        ? { position: 'absolute', ...emodjiPickerPosition }
                        : {
                            position: 'absolute',
                            bottom: '25px',
                            right: '-250px',
                          }
                    }
                  >
                    {emojiPicker}
                  </Box>
                )}
              </ClickAwayListener>
            )}
          </Stack>
        )}

        <style>
          {`
            .mention:not([data-id]) {
              color: ${theme.palette.primary.main} !important;
              background-color: transparent !important;
              border-radius: 0 !important;
              padding: 0 !important;
              text-decoration: underline !important;
            }
            
            @media (max-width: 600px) {
              .tiptap {
                /* Prevent iOS zoom on input focus */
                -webkit-text-size-adjust: 100%;
                /* Improve touch scrolling */
                -webkit-overflow-scrolling: touch;
                /* Better touch interaction */
                touch-action: manipulation;
              }
              
              .ProseMirror {
                /* Ensure proper mobile behavior */
                user-select: text;
                -webkit-user-select: text;
                min-height: inherit;
              }
            }
          `}
        </style>

        {note && (
          <Box sx={{ position: 'absolute', bottom: clearText ? '25px' : '0px' }}>{note}</Box>
        )}
        {!disabled && !hideToolButtons && !isExternalToolbar && !isMobile && toolbar}
        {clearText && (
          <Stack direction="row" justifyContent="end" marginRight={'15px'}>
            <MuiLink
              disabled={disabled}
              component="button"
              variant="body2"
              onClick={() => {
                if (editor) {
                  editor.commands.clearContent();
                  clearText();
                }
              }}
            >
              Clear
            </MuiLink>
          </Stack>
        )}
      </Box>

      <EditShortLinkModal
        open={openUrlShortenerModal}
        onClose={() => setOpenUrlShortenerModal(false)}
        onCreate={handleInsertShortUrl}
        disablePortal={true}
      />

      {!disabled &&
        !hideToolButtons &&
        isExternalToolbar &&
        toolbarContainer &&
        createPortal(toolbar, toolbarContainer)}
    </>
  );
}
